/**
 * Tracks an invite token a visitor arrived with (e.g. `/?invite=ABC123`).
 *
 * The token is captured once on load and stashed in localStorage so it
 * survives the unauthenticated → authenticated flip (the OnboardingFlow tree
 * unmounts the instant auth resolves). The `?invite=` query param is stripped
 * from the URL right after capture so a reload or share of the address bar
 * doesn't re-trigger or leak the code.
 */

const STORAGE_KEY = 'babysteps.pending_invite';
const PARAM = 'invite';

let captured = false;

/** Read the token from the URL (if present), persist it, and clean the URL. */
function captureFromUrl(): void {
  if (captured) return;
  captured = true;
  try {
    const url = new URL(window.location.href);
    const token = url.searchParams.get(PARAM);
    if (token && token.trim() !== '') {
      localStorage.setItem(STORAGE_KEY, token.trim());
      url.searchParams.delete(PARAM);
      window.history.replaceState({}, '', url.pathname + url.search + url.hash);
    }
  } catch {
    /* no-op — non-browser env or storage disabled */
  }
}

/** The pending invite token, or null. Captures from the URL on first call. */
export function getPendingInvite(): string | null {
  captureFromUrl();
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v && v.trim() !== '' ? v : null;
  } catch {
    return null;
  }
}

/**
 * Normalise user-entered input into a bare token. Accepts a raw code or a full
 * invite URL (`…/?invite=TOKEN`). Returns null for empty input.
 */
export function parseInviteToken(raw: string): string | null {
  const v = raw.trim();
  if (v === '') return null;
  try {
    if (v.includes('://') || v.startsWith('/?') || v.includes('?invite=')) {
      const url = new URL(v, window.location.origin);
      const q = url.searchParams.get(PARAM);
      if (q && q.trim() !== '') return q.trim();
    }
  } catch {
    /* not a URL — treat as a bare code */
  }
  return v;
}

/** Clear the stored token once it has been accepted (or declined). */
export function clearPendingInvite(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* no-op */
  }
}
